import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import * as XLSX from "xlsx";

import { HttpMainApi } from "../../interface/main-api";
import { userState } from "../../interface/MainInterface";
import LoadingCircle from "../../utils/LoadingCircle";

interface PropsType {
  userState: userState;
}

interface MonthOption {
  value: string;
  label: string;
}

interface IntegratedAccountApiRow {
  userAddr: unknown;
  golf: unknown;
  health: unknown;
  study: unknown;
  locker: unknown;
  screenRoom: unknown;
  bookCafe: unknown;
  tableTennis?: unknown;
  robotCleaner: unknown;
  total: unknown;
}

interface IntegratedAccountRow {
  userAddr: string;
  golf: number;
  health: number;
  study: number;
  locker: number;
  screenRoom: number;
  bookCafe: number;
  tableTennis: number;
  robotCleaner: number;
  total: number;
}

const mainApi = new HttpMainApi();
const BILLING_STATE = "신청";

const columns: GridColDef[] = [
  {
    field: "userAddr",
    headerName: "동/호",
    minWidth: 170,
    flex: 1.4,
    sortable: true,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "golf",
    headerName: "골프",
    minWidth: 90,
    flex: 1,
    align: "right",
    headerAlign: "center",
  },
  {
    field: "health",
    headerName: "헬스",
    minWidth: 90,
    flex: 1,
    align: "right",
    headerAlign: "center",
  },
  {
    field: "study",
    headerName: "독서실",
    minWidth: 90,
    flex: 1,
    align: "right",
    headerAlign: "center",
  },
  {
    field: "locker",
    headerName: "락커",
    minWidth: 90,
    flex: 1,
    align: "right",
    headerAlign: "center",
  },
  {
    field: "screenRoom",
    headerName: "스크린 룸",
    minWidth: 110,
    flex: 1,
    align: "right",
    headerAlign: "center",
  },
  {
    field: "bookCafe",
    headerName: "북카페",
    minWidth: 100,
    flex: 1,
    align: "right",
    headerAlign: "center",
  },
  {
    field: "tableTennis",
    headerName: "탁구장",
    minWidth: 100,
    flex: 1,
    align: "right",
    headerAlign: "center",
  },
  {
    field: "robotCleaner",
    headerName: "로봇청소기",
    minWidth: 120,
    flex: 1,
    align: "right",
    headerAlign: "center",
  },
  {
    field: "total",
    headerName: "총계",
    minWidth: 110,
    flex: 1.1,
    align: "right",
    headerAlign: "center",
  },
];

function generateMonths(): MonthOption[] {
  const currentDate = new Date();
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1 - index, 1);
    const value = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}`;
    return {
      value,
      label: `${date.getFullYear()}년 ${String(date.getMonth() + 1).padStart(2, "0")}월`,
    };
  });
}

function toNumericAmount(value: unknown): number {
  if (
    (typeof value !== "number" && typeof value !== "string") ||
    (typeof value === "string" && !value.trim())
  ) {
    throw new Error("Invalid integrated account amount");
  }
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount < 0) {
    throw new Error("Invalid integrated account amount");
  }
  return amount;
}

function normalizeRows(result: unknown): IntegratedAccountRow[] {
  if (!Array.isArray(result)) {
    throw new Error("Invalid integrated account result");
  }

  return result
    .map((item): IntegratedAccountRow => {
      if (!item || typeof item !== "object") {
        throw new Error("Invalid integrated account row");
      }

      const row = item as IntegratedAccountApiRow;
      if (typeof row.userAddr !== "string" || !row.userAddr.trim()) {
        throw new Error("Invalid integrated account address");
      }

      const normalizedRow = {
        userAddr: row.userAddr.trim(),
        golf: toNumericAmount(row.golf),
        health: toNumericAmount(row.health),
        study: toNumericAmount(row.study),
        locker: toNumericAmount(row.locker),
        screenRoom: toNumericAmount(row.screenRoom),
        bookCafe: toNumericAmount(row.bookCafe),
        tableTennis: toNumericAmount(row.tableTennis ?? 0),
        robotCleaner: toNumericAmount(row.robotCleaner),
        total: toNumericAmount(row.total),
      };

      const calculatedTotal =
        normalizedRow.golf +
        normalizedRow.health +
        normalizedRow.study +
        normalizedRow.locker +
        normalizedRow.screenRoom +
        normalizedRow.bookCafe +
        normalizedRow.tableTennis +
        normalizedRow.robotCleaner;
      if (normalizedRow.total !== calculatedTotal) {
        throw new Error("Integrated account total mismatch");
      }

      return normalizedRow;
    })
    .sort((left, right) => left.userAddr.localeCompare(right.userAddr, "ko-KR", { numeric: true }));
}

const IntegratedAccountList = (_props: PropsType) => {
  const months = useMemo(generateMonths, []);
  const [selectMonth, setSelectMonth] = useState(months[1]?.value ?? "");
  const [rows, setRows] = useState<IntegratedAccountRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const totalPrice = useMemo(
    () => rows.reduce((sum, row) => sum + row.total, 0),
    [rows],
  );

  const resetResult = () => {
    setRows([]);
    setHasSearched(false);
    setErrorMessage("");
  };

  const handleMonthChange = (event: SelectChangeEvent) => {
    setSelectMonth(event.target.value);
    resetResult();
  };

  const getIntegratedAccountList = async () => {
    if (!selectMonth) return;

    setIsLoading(true);
    resetResult();

    try {
      const response = await mainApi.get_integrated_account_breakdown({
        selectMonth,
        selectState: BILLING_STATE,
      });

      if (response.code !== "200") {
        throw new Error(response.response?.error_msg || "Integrated account request failed");
      }
      if (
        response.response?.billingMonth !== selectMonth ||
        response.response?.billingState !== BILLING_STATE
      ) {
        throw new Error("Integrated account billing criteria mismatch");
      }

      setRows(normalizeRows(response.response?.result));
      setHasSearched(true);
    } catch (error) {
      console.error("IntegratedAccountList load failed", error);
      setErrorMessage("통합청구서를 불러오지 못했습니다. 잠시 후 다시 조회해 주세요.");
    } finally {
      setIsLoading(false);
    }
  };

  const downloadExcel = () => {
    const dataForExcel = rows.map((row) => ({
      "동/호": row.userAddr,
      골프: row.golf,
      헬스: row.health,
      독서실: row.study,
      락커: row.locker,
      "스크린 룸": row.screenRoom,
      북카페: row.bookCafe,
      탁구장: row.tableTennis,
      로봇청소기: row.robotCleaner,
      총계: row.total,
    }));
    const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "IntegratedAccounts");
    XLSX.writeFile(workbook, "integrated-accounts.xlsx");
  };

  return (
    <Stack direction="column" spacing={2} sx={{ p: 4 }}>
      <Box className="sub-header-root">
        <Typography component="h1" variant="h5" gutterBottom className="sub-header-title">
          통합청구서
        </Typography>
      </Box>

      <Alert severity="info" role="note">
        북카페·탁구장·로봇청소기 사용료는 선택한 기준월의 신청 청구 금액에 포함됩니다.
      </Alert>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel id="integrated-account-month-label">기준월</InputLabel>
          <Select
            labelId="integrated-account-month-label"
            value={selectMonth}
            label="기준월"
            onChange={handleMonthChange}
            sx={{ height: 46 }}
          >
            {months.map((month) => (
              <MenuItem key={month.value} value={month.value}>
                {month.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography component="p" sx={{ minWidth: 112 }}>
          청구 상태: {BILLING_STATE}
        </Typography>
        <Button
          variant="contained"
          onClick={() => void getIntegratedAccountList()}
          disabled={isLoading || !selectMonth}
          startIcon={<SearchIcon />}
          aria-label="통합청구서 조회"
          sx={{ height: 46 }}
        >
          조회
        </Button>
        <Button
          variant="contained"
          onClick={downloadExcel}
          disabled={isLoading || rows.length === 0}
          startIcon={<FileDownloadIcon />}
          aria-label="통합청구서 엑셀 다운로드"
          sx={{ height: 46 }}
        >
          EXCEL
        </Button>
      </Stack>

      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

      {hasSearched && !errorMessage && rows.length === 0 ? (
        <Typography component="p" sx={{ py: 8, textAlign: "center", color: "text.secondary" }}>
          선택한 기준월에 청구할 내역이 없습니다.
        </Typography>
      ) : null}

      {rows.length > 0 ? (
        <>
          <Box data-testid="integrated-account-grid-scroll" sx={{ width: "100%", overflowX: "auto" }}>
            <Box sx={{ minWidth: 970 }}>
              <DataGrid
                rows={rows}
                columns={columns}
                disableRowSelectionOnClick
                style={{ height: 700 }}
                getRowId={(row) => row.userAddr}
              />
            </Box>
          </Box>
          <Typography component="p">총 금액: {totalPrice.toLocaleString("ko-KR")} 원</Typography>
        </>
      ) : null}

      <LoadingCircle loading={isLoading} />
    </Stack>
  );
};

export default IntegratedAccountList;
